﻿import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Badge } from '@/components/ui/badge';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { leaveService } from '@/services/leave.service';
import { penaltyService } from '@/services/penalty.service';
import { paidHoursService } from '@/services/paid-hours.service';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function SectionTitle({ title, count }: { title: string; count: number }) {
  return (
    <div className="flex items-center justify-between py-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      {count > 0 && <Badge variant="warning">{count}</Badge>}
    </div>
  );
}

function SkeletonRows({ count = 2 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded-lg bg-muted/50" />
      ))}
    </div>
  );
}

function LeaveSection() {
  const { has } = usePermission();

  const leaveQ = useQuery({
    queryKey: ['dashboard', 'approvals', 'leave'],
    queryFn: () => leaveService.listTeamRequests({ status: 'pending', page: 1, limit: 3 }),
    enabled: has(PERMISSIONS.LEAVE_APPROVE),
  });

  const items = leaveQ.data?.data ?? [];
  const total = leaveQ.data?.pagination?.total ?? 0;

  return (
    <div className="px-4 py-3">
      <SectionTitle title="Leave Requests" count={total} />
      {leaveQ.isLoading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <p className="py-2 text-xs text-muted-foreground">No pending leave requests</p>
      ) : (
        <div className="space-y-2">
          {items.map((req) => (
            <div key={req.id} className="flex items-center justify-between text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {req.target_first_name} {req.target_last_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {req.leave_type_name}  {formatDate(req.start_date)}
                  {req.end_date !== req.start_date ? ` â†’ ${formatDate(req.end_date)}` : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      <Link to="/hrms/leave" className="mt-2 block text-xs text-primary hover:underline">
        Review in Leave Approvals
      </Link>
    </div>
  );
}

function PenaltySection() {
  const { has } = usePermission();

  const penaltyQ = useQuery({
    queryKey: ['dashboard', 'approvals', 'penalty'],
    queryFn: () => penaltyService.list({ status: 'pending', page: 1, limit: 3 }),
    enabled: has(PERMISSIONS.PENALTY_MANAGE),
  });

  const items = penaltyQ.data?.data ?? [];
  const total = penaltyQ.data?.pagination?.total ?? 0;

  return (
    <div className="px-4 py-3">
      <SectionTitle title="Penalty Approvals" count={total} />
      {penaltyQ.isLoading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <p className="py-2 text-xs text-muted-foreground">No pending penalties</p>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {p.first_name} {p.last_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(p.attendance_date)}  {p.penalty_minutes} min
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      <Link to="/hrms/penalty" className="mt-2 block text-xs text-primary hover:underline">
        Review in Penalties
      </Link>
    </div>
  );
}

function PaidHoursSection() {
  const { has } = usePermission();

  const paidHoursQ = useQuery({
    queryKey: ['dashboard', 'approvals', 'paid-hours'],
    queryFn: () => paidHoursService.list({ status: 'pending', page: 1, limit: 3 }),
    enabled: has(PERMISSIONS.PAID_HOURS_MANAGE),
  });

  const items = paidHoursQ.data?.data ?? [];
  const total = paidHoursQ.data?.pagination?.total ?? 0;

  return (
    <div className="px-4 py-3">
      <SectionTitle title="Paid Hours" count={total} />
      {paidHoursQ.isLoading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <p className="py-2 text-xs text-muted-foreground">No pending paid hours</p>
      ) : (
        <div className="space-y-2">
          {items.map((ph) => (
            <div key={ph.id} className="flex items-center justify-between text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {ph.first_name} {ph.last_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(ph.attendance_date)} {ph.hours}h
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      <Link to="/hrms/attendance" className="mt-2 block text-xs text-primary hover:underline">
        Review in Attendance
      </Link>
    </div>
  );
}

export function ApprovalsQueueWidget() {
  const { has } = usePermission();

  const hasAnyApproval =
    has(PERMISSIONS.LEAVE_APPROVE) ||
    has(PERMISSIONS.PENALTY_MANAGE) ||
    has(PERMISSIONS.PAID_HOURS_MANAGE);

  if (!hasAnyApproval) return null;

  return (
    <Card className="shadow-card">
      <SectionHeader icon={<Inbox />} title="Approvals Queue" hint="Pending actions" />
      <CardContent className="p-0">
        <div className="divide-y divide-border/40">
          <PermissionGate permission={PERMISSIONS.LEAVE_APPROVE}>
            <LeaveSection />
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.PENALTY_MANAGE}>
            <PenaltySection />
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.PAID_HOURS_MANAGE}>
            <PaidHoursSection />
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
